// Kênh nhân sự (hợp đồng Marketing kenh-nhan-su-erp-v1) — ba cửa /me/staff-channel*.
// Máy chủ lấy danh tính từ phiên đăng nhập: KHÔNG truyền mã người nào lên (CEO 7a).
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiBaseUrl } from "@/lib/api/client";
import { postFormAuthed } from "@/lib/api/authed-fetch";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { useStaffChannelFlag } from "./flag";

export type StaffChannelView = components["schemas"]["StaffChannelView"];
export type StaffChannelSummary = components["schemas"]["StaffChannelSummary"];
export type StaffChannelPolicy = components["schemas"]["StaffChannelPolicy"];
export type StaffChannelCommand = components["schemas"]["StaffChannelCommand"];
export type StaffChannelCommandInput = components["schemas"]["StaffChannelCommandInput"];
export type StaffChannelProofReceipt = components["schemas"]["StaffChannelProofReceipt"];
export type SummaryChannel = StaffChannelSummary["channels"][number];
export type SummaryVideo = StaffChannelSummary["videos"][number];
export type SummaryLedger = NonNullable<StaffChannelSummary["ledger"]>[number];

export const staffChannelKey = ["me", "staff-channel"] as const;

/** Có lệnh còn "đang gửi" thì hỏi lại mỗi 5 giây cho tới khi Marketing trả lời. */
export function pollEvery(view: StaffChannelView | undefined): number | false {
  return view?.commands?.some((c) => c.state === "queued") ? 5_000 : false;
}

/** Bản sao Kênh nhân sự của CHÍNH người đăng nhập. */
export function useMyStaffChannel(enabled = true) {
  return useQuery({
    queryKey: staffChannelKey,
    enabled,
    staleTime: 30_000,
    refetchInterval: (q) => pollEvery(q.state.data),
    queryFn: async () => {
      const { data, error } = await api.GET("/me/staff-channel", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được Kênh nhân sự"));
      return data;
    },
  });
}

/** Nạp công tắc màn cho menu (đọc đồng bộ qua useStaffChannelFlag). */
export function useStaffChannelFlagSync(loggedIn: boolean) {
  const q = useMyStaffChannel(loggedIn);
  const setEnabled = useStaffChannelFlag((s) => s.setEnabled);
  const on = loggedIn && !!q.data?.enabled;
  useEffect(() => setEnabled(on), [on, setEnabled]);
}

/**
 * Mã thao tác — sinh MỘT lần mỗi lần bấm (hợp đồng §3). Bấm lại khi mạng chập
 * phải gửi lại ĐÚNG mã đó, nên hộp khai giữ mã tới khi lệnh vào hàng.
 */
export function newOperationId(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return "kns-" + Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

/** Gửi một lệnh. 202 = đã vào hàng Marketing, chưa phải thành công. */
export function useSendStaffChannelCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: StaffChannelCommandInput) => {
      const { data, error } = await api.POST("/me/staff-channel/commands", { body });
      if (error || !data)
        throw new Error(serverErrorMessage(error, "Chưa gửi được lệnh sang Marketing"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: staffChannelKey }),
  });
}

/** Tải một ảnh chứng minh / ảnh số liệu — ảnh đi thẳng sang Marketing qua Center. */
export async function uploadStaffChannelProof(file: File): Promise<StaffChannelProofReceipt> {
  const form = new FormData();
  form.append("file", file);
  const res = await postFormAuthed(`${apiBaseUrl}/me/staff-channel/proofs`, form);
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new Error(serverErrorMessage(body, "Chưa gửi được ảnh — thử lại sau ít phút"));
  return body as StaffChannelProofReceipt;
}
